(() => {
  'use strict';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const sliders = new Set();
  function initCinema(root = document) {
    root.querySelectorAll('[data-vr-cinema]').forEach(el => {
      if (el.dataset.initialized) return;
      el.dataset.initialized = 'true';
      const slides = [...el.querySelectorAll('[data-vr-slide]')];
      const tabs = [...el.querySelectorAll('[data-vr-goto]')];
      const play = el.querySelector('[data-vr-play]');
      let active = 0, timer, paused = reduced.matches || Boolean(navigator.connection?.saveData), visible = true, hover = false, focused = false;
      const interval = Number(el.dataset.interval) || 10000;
      el.style.setProperty('--vr-slide-time', `${interval}ms`);
      const stop = () => { clearTimeout(timer); slides.forEach(slide => slide.querySelector('video')?.pause()); el.classList.remove('is-playing'); };
      const run = () => {
        stop();
        play.setAttribute('aria-pressed', String(paused));
        play.setAttribute('aria-label', paused ? play.dataset.play : play.dataset.pause);
        play.querySelector('[data-vr-play-icon]').textContent = paused ? '▶' : 'Ⅱ';
        if (paused || !visible || hover || focused || document.hidden) return;
        const video = slides[active]?.querySelector('video');
        if (video) {
          if (!video.src) video.src = window.matchMedia('(max-width: 767px)').matches ? video.dataset.mobileSrc : video.dataset.src;
          video.muted = true;
          video.play().catch(() => {});
        }
        el.classList.add('is-playing');
        if (slides.length > 1) timer = setTimeout(() => show(active + 1), interval);
      };
      const show = (index, manual = false) => {
        active = (index + slides.length) % slides.length;
        slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === active);
          slide.inert = i !== active;
          slide.setAttribute('aria-hidden', String(i !== active));
        });
        tabs.forEach((tab, i) => tab.setAttribute('aria-current', String(i === active)));
        if (manual) {
          paused = true;
          el.querySelector('[data-vr-announcement]').textContent = tabs[active]?.textContent.trim() || '';
        }
        run();
      };
      tabs.forEach((tab, i) => tab.addEventListener('click', () => show(i, true)));
      el.querySelector('[data-vr-previous]')?.addEventListener('click', () => show(active - 1, true));
      el.querySelector('[data-vr-next]')?.addEventListener('click', () => show(active + 1, true));
      play.addEventListener('click', () => { paused = !paused; el.toggleAttribute('data-motion-enabled', !paused); hover = false; focused = false; run(); });
      el.addEventListener('focusin', event => { if (event.target !== play && el.querySelector('.vr-cinema__controls')?.contains(event.target)) { focused = true; run(); } });
      el.addEventListener('focusout', event => { if (!el.contains(event.relatedTarget)) { focused = false; run(); } });
      el.addEventListener('keydown', event => {
        if (!event.target.closest('.vr-cinema__controls')) return;
        if (event.key === 'ArrowRight') { event.preventDefault(); show(active + 1, true); tabs[active].focus(); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); show(active - 1, true); tabs[active].focus(); }
      });
      const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; run(); }, { threshold: .15 });
      observer.observe(el);
      const handleVisibility = () => run();
      const handlePreference = () => { paused = reduced.matches; run(); };
      document.addEventListener('visibilitychange', handleVisibility);
      reduced.addEventListener('change', handlePreference);
      const instance = { el, destroy: () => { stop(); observer.disconnect(); document.removeEventListener('visibilitychange', handleVisibility); reduced.removeEventListener('change', handlePreference); } };
      sliders.add(instance);
      show(0);
    });
  }
  function initReveals(root = document) {
    if (reduced.matches || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) { entry.target.classList.add('is-revealed'); observer.unobserve(entry.target); }
    }), { threshold: .08 });
    root.querySelectorAll('[data-vr-reveal]').forEach(el => { el.classList.add('vr-reveal-ready'); observer.observe(el); });
  }
  function initDetails(root = document) {
    root.querySelectorAll('[data-vr-detail-link]').forEach(link => {
      if (link.dataset.bound) return;
      link.dataset.bound = 'true';
      link.addEventListener('click', () => {
        const target = document.getElementById(link.hash.slice(1));
        if (target?.tagName === 'DETAILS') target.open = true;
      });
    });
  }
  function initContact(root = document) {
    const select = root.querySelector('[data-vr-contact-select]');
    if (!select) return;
    const form = select.closest('form');
    const choose = value => { if ([...select.options].some(option => option.value === value)) select.value = value; };
    const params = new URLSearchParams(location.search);
    if (params.has('topic')) choose(params.get('topic'));
    const reference = form.querySelector('input[name="contact[Product or order reference]"]');
    if (reference && params.has('product')) reference.value = params.get('product').slice(0,500);
    root.querySelectorAll('[data-vr-contact-topic]').forEach(link => {
      if (link.dataset.bound) return;
      link.dataset.bound = 'true';
      link.addEventListener('click', event => {
        event.preventDefault();
        choose(link.dataset.vrContactTopic);
        document.getElementById('vr-contact-form').scrollIntoView({behavior: reduced.matches ? 'instant' : 'smooth',block:'start'});
        select.focus({preventScroll:true});
      });
    });
  }
  function initCategorySliders(root = document) {
    root.querySelectorAll('[data-vr-category-slider]').forEach(el => {
      if (el.dataset.sliderReady) return;
      el.dataset.sliderReady = 'true';
      const track = el.querySelector('[data-vr-category-track]');
      const cards = [...track.querySelectorAll('.vr-research-card')];
      const previous = el.querySelector('[data-vr-category-prev]');
      const next = el.querySelector('[data-vr-category-next]');
      const activate = card => cards.forEach(item => item.classList.toggle('is-expanded', item === card));
      const move = direction => {
        track.scrollBy({left: direction * track.clientWidth * .7, behavior: reduced.matches ? 'instant' : 'smooth'});
      };
      const update = () => {
        previous.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      };
      cards.forEach((card, index) => {
        card.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') activate(card); });
        card.addEventListener('focus', () => activate(card));
        card.addEventListener('keydown', event => {
          if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
          event.preventDefault();
          const position = event.key === 'Home' ? 0 : event.key === 'End' ? cards.length - 1 : Math.max(0, Math.min(cards.length - 1, index + (event.key === 'ArrowRight' ? 1 : -1)));
          cards[position].focus({preventScroll:true});
          cards[position].scrollIntoView({behavior:reduced.matches ? 'instant' : 'smooth',block:'nearest',inline:'nearest'});
        });
      });
      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      track.addEventListener('scroll', update, {passive:true});
      track.addEventListener('transitionend', update);
      activate(cards[0]);
      update();
    });
  }
  function initCategoryPickers(root = document) {
    root.querySelectorAll('select[data-vareum-category]').forEach(select => {
      if (select.dataset.pickerReady) return;
      select.dataset.pickerReady = 'true';
      const wrap = document.createElement('div');
      wrap.className = 'vr-category-picker';
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'vr-category-picker__toggle';
      toggle.setAttribute('aria-expanded', 'false');
      const panel = document.createElement('div');
      panel.className = 'vr-category-picker__panel';
      panel.id = `${select.id}-choices`;
      panel.hidden = true;
      panel.setAttribute('role', 'group');
      panel.setAttribute('aria-label', select.getAttribute('aria-label') || 'Categories');
      toggle.setAttribute('aria-controls', panel.id);
      const options = [...select.options];
      const buttons = options.map(option => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = option.textContent;
        button.setAttribute('aria-pressed', String(option.selected));
        button.addEventListener('click', () => {
          select.value = option.value;
          select.dispatchEvent(new Event('change', {bubbles:true}));
          close();
          toggle.focus();
        });
        panel.append(button);
        return button;
      });
      const update = () => {
        const selected = select.selectedOptions[0];
        toggle.textContent = selected?.textContent || options[0].textContent;
        toggle.setAttribute('aria-label', `${select.getAttribute('aria-label')}: ${toggle.textContent}`);
        buttons.forEach((button,i) => button.setAttribute('aria-pressed', String(options[i].value === select.value)));
      };
      const close = () => {panel.hidden = true;toggle.setAttribute('aria-expanded','false');};
      toggle.addEventListener('click', () => {panel.hidden = !panel.hidden;toggle.setAttribute('aria-expanded',String(!panel.hidden));});
      wrap.addEventListener('keydown', event => {
        if (event.key === 'Escape') {event.preventDefault();close();toggle.focus();return;}
        if (!['ArrowDown','ArrowUp','Home','End'].includes(event.key)) return;
        event.preventDefault();
        panel.hidden = false;toggle.setAttribute('aria-expanded','true');
        const index = buttons.indexOf(document.activeElement);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length-1 : Math.max(0,Math.min(buttons.length-1,index+(event.key === 'ArrowDown' ? 1 : -1)));
        buttons[next].focus();
      });
      wrap.addEventListener('focusout', event => {if (!wrap.contains(event.relatedTarget)) close();});
      document.addEventListener('click', event => {if (!wrap.contains(event.target)) close();});
      select.addEventListener('change', update);
      wrap.append(toggle,panel);
      select.insertAdjacentElement('afterend',wrap);
      select.hidden = true;
      select.closest('.search__types').classList.add('vr-has-picker');
      update();
    });
  }
  function initMegaMenus(root) {
    root.querySelectorAll('[data-vr-mega]').forEach(menu => {
      if (menu.dataset.ready) return;
      menu.dataset.ready = 'true';
      const tabs = [...menu.querySelectorAll('[data-vr-mega-tab]')];
      const panels = [...menu.querySelectorAll('.vr-mega__category-panel')];
      if (!tabs.length) return;
      const select = document.createElement('select');
      select.className = 'vr-mega__select';
      select.id = panels[0].id + '-select';
      const label = document.createElement('label');
      label.className = 'vr-mega__select-label';
      label.htmlFor = select.id;
      label.textContent = menu.querySelector('.vr-mega__categories').getAttribute('aria-label');
      const activate = index => {
        tabs.forEach((tab,i) => {tab.classList.toggle('is-active',i === index);tab.setAttribute('aria-pressed',String(i === index));panels[i].hidden = i !== index;});
        select.selectedIndex = index;
      };
      tabs.forEach((tab,index) => {
        tab.removeAttribute('aria-current');
        tab.setAttribute('role','button');
        tab.setAttribute('aria-pressed',String(index === 0));
        const option = document.createElement('option');
        option.textContent = tab.querySelector('span').textContent;
        option.value = String(index);
        select.append(option);
        tab.addEventListener('click',event=>{event.preventDefault();activate(index);});
        tab.addEventListener('keydown',event=>{
          if (event.key === ' ') {event.preventDefault();activate(index);}
          if (!['ArrowUp','ArrowDown','Home','End'].includes(event.key)) return;
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length-1 : (index+(event.key === 'ArrowDown' ? 1 : tabs.length-1))%tabs.length;
          activate(next);tabs[next].focus();
        });
      });
      select.addEventListener('change',()=>activate(select.selectedIndex));
      menu.querySelector('.vr-mega__content').prepend(label,select);
      menu.classList.add('is-enhanced');
      menu.addEventListener('keydown',event=>{if(event.key==='Escape'){menu.open=false;menu.querySelector('summary').focus();event.stopPropagation();}});
      document.addEventListener('click',event=>{if(!menu.contains(event.target)) menu.open=false;});
      menu.addEventListener('focusout',event=>{if(event.relatedTarget&&!menu.contains(event.relatedTarget)) menu.open=false;});
    });
  }
  function initResourceMenus(root) {
    root.querySelectorAll('.vr-resource-menu').forEach(menu=>{
      if(menu.dataset.ready)return;
      menu.dataset.ready='true';
      menu.addEventListener('keydown',event=>{if(event.key==='Escape'){menu.open=false;menu.querySelector('summary').focus();event.stopPropagation();}});
      document.addEventListener('click',event=>{if(!menu.contains(event.target))menu.open=false;});
      menu.addEventListener('focusout',event=>{if(event.relatedTarget&&!menu.contains(event.relatedTarget))menu.open=false;});
    });
  }
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-vr-copy-sku]');
    if (!button || button.disabled) return;
    const row = button.closest('.product__sku');
    const value = row?.querySelector('[data-vr-sku-value]')?.textContent.trim();
    const feedback = row?.querySelector('[data-vr-sku-feedback]');
    if (!value || !feedback) return;
    button.disabled = true;
    clearTimeout(button.vrFeedbackTimer);
    try {
      try {
        await navigator.clipboard.writeText(value);
      } catch {
        const field = document.createElement('textarea');
        field.value = value;
        field.style.cssText = 'position:fixed;top:0;left:-9999px;font-size:16px';
        document.body.append(field);
        field.select();
        try { if (!document.execCommand('copy')) throw new Error('Copy unavailable'); }
        finally { field.remove(); button.focus({preventScroll:true}); }
      }
      feedback.textContent = button.dataset.copiedLabel;
      button.dataset.copied = 'true';
    } catch {
      feedback.textContent = button.dataset.errorLabel;
    } finally {
      button.disabled = false;
      button.vrFeedbackTimer = setTimeout(() => { feedback.textContent = ''; delete button.dataset.copied; }, 2500);
    }
  });

  /* Manufacturer product preview: fetch only products with photos; matching vendor only. */
  const brandProductCache=new Map();
  const brandEscape=s=>String(s||'').replace(/[&<>"']/g,c=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  async function brandProducts(vendor){
    const key=vendor.trim().toLowerCase();
    if(brandProductCache.has(key))return brandProductCache.get(key);
    const task=(async()=>{
      const url='/search/suggest.json?q='+encodeURIComponent(vendor)+'&resources[type]=product&resources[limit]=10';
      const response=await fetch(url,{credentials:'same-origin'});
      if(!response.ok)throw new Error('Search unavailable');
      const data=await response.json();
      const list=data?.resources?.results?.products||[];
      return list.filter(p=>p.image&&(p.vendor||'').toLowerCase()===key).slice(0,4);
    })();
    brandProductCache.set(key,task);
    try{return await task}catch(e){brandProductCache.delete(key);throw e}
  }
  let brandHoverTimer;
  async function openBrandPreview(link){
    const area=link.closest('.vr-mega__category-panel');
    const pocket=area?.querySelector('[data-vr-brand-pocket]');
    if(!pocket)return;
    const vendor=link.dataset.vrBrand;
    if(!vendor)return;
    pocket.hidden=false;
    pocket.dataset.requestedVendor=vendor;
    pocket.querySelector('[data-vr-brand-title]').textContent=link.dataset.vrBrandLabel||vendor;
    const all=pocket.querySelector('[data-vr-brand-all]');
    all.href=link.getAttribute('href')||'#';
    all.target=link.target==='_blank'?'_blank':'_self';
    all.rel=link.target==='_blank'?'noopener noreferrer':'';
    const results=pocket.querySelector('[data-vr-brand-results]');
    results.innerHTML='<p class="vr-brand-pocket__message">Loading selected products…</p>';
    try{
      const products=await brandProducts(vendor);
      if(pocket.dataset.requestedVendor!==vendor)return;
      results.textContent='';
      if(!products.length){results.innerHTML='<p class="vr-brand-pocket__message">No products with images found in quick search. Explore the complete manufacturer catalog.</p>';return}
      products.forEach(p=>{
        const a=document.createElement('a');a.className='vr-brand-product';a.href=p.url||'#';
        const im=document.createElement('img');im.src=p.image;im.alt=p.title||'';im.loading='lazy';
        const title=document.createElement('span');title.className='vr-brand-product__title';title.textContent=p.title||'Product';
        a.append(im,title);results.append(a);
      });
    }catch{if(pocket.dataset.requestedVendor===vendor)results.innerHTML='<p class="vr-brand-pocket__message">Products are temporarily unavailable. Use Explore manufacturer.</p>'}
  }
  document.addEventListener('mouseover',event=>{
    if(!window.matchMedia('(hover:hover)').matches)return;
    const link=event.target.closest('[data-vr-brand-link]');
    if(!link||!link.closest('.vr-mega__category-panel'))return;
    clearTimeout(brandHoverTimer);
    brandHoverTimer=setTimeout(()=>openBrandPreview(link),65);
  });
  document.addEventListener('click',event=>{
    const close=event.target.closest('[data-vr-brand-close]');
    if(close){const pocket=close.closest('[data-vr-brand-pocket]');if(pocket){pocket.hidden=true;delete pocket.dataset.requestedVendor}return}
    const link=event.target.closest('[data-vr-brand-link]');
    if(!link||!link.closest('.vr-mega__category-panel'))return;
    event.preventDefault();openBrandPreview(link);
  });


  function initInitialBrandProducts(root=document){
    root.querySelectorAll('.vr-mega__category-panel [data-vr-brand-pocket]').forEach(pocket=>{
      if(pocket.dataset.initialized)return;
      pocket.dataset.initialized='true';
      const panel=pocket.closest('.vr-mega__category-panel');
      const first=panel?.querySelector('[data-vr-brand-link]');
      if(!first)return;
      const start=()=>{if(!pocket.hidden&&!panel.hidden){const results=pocket.querySelector('[data-vr-brand-results]');if(!results.querySelector('.vr-brand-product'))openBrandPreview(first)}};
      const mega=pocket.closest('[data-vr-mega]');
      if(mega)mega.addEventListener('toggle',()=>{if(mega.open){brandProducts(first.dataset.vrBrand).catch(()=>{});start()}});
      panel.addEventListener('pointerenter',start);
      panel.addEventListener('focusin',start);
      if(mega?.open)start();
    });
  }
  const init = root => { initCinema(root); initReveals(root); initDetails(root); initContact(root); initCategorySliders(root); initCategoryPickers(root); initMegaMenus(root); initResourceMenus(root); };
  init(document);
  document.addEventListener('shopify:section:load', e => init(e.target));
  document.addEventListener('shopify:section:unload', e => sliders.forEach(slider => { if (e.target.contains(slider.el)) { slider.destroy(); sliders.delete(slider); } }));
})();
